
import React from 'react';
import { Link } from 'react-router-dom';

const InfoBox = ({ title, desc, icon }: { title: string, desc: string, icon: string }) => (
  <div className="group p-10 bg-white border border-slate-200 rounded-none hover:border-black transition-all duration-500">
    <div className="w-14 h-14 bg-slate-50 rounded-none flex items-center justify-center mb-8 group-hover:bg-black transition-all">
      <i className={`fas ${icon} text-slate-900 group-hover:text-white text-xl`}></i>
    </div>
    <h3 className="text-lg font-black text-slate-900 mb-4 uppercase tracking-tighter">{title}</h3>
    <p className="text-sm text-slate-500 leading-relaxed font-medium">{desc}</p>
  </div>
);

const ReportCard = ({ title, date, id, img }: { title: string, date: string, id: string, img: string }) => (
  <Link to={`/findings`} className="group flex flex-col space-y-4">
    <div className="aspect-[4/5] bg-slate-100 overflow-hidden relative">
      <img 
        src={img} 
        alt={title} 
        className="w-full h-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105 group-hover:shadow-[0_0_50px_rgba(249,115,22,0.4)]"
      />
      <div className="absolute inset-0 bg-orange-500/0 group-hover:bg-orange-500/10 transition-colors pointer-events-none"></div>
      <div className="absolute top-4 left-4 bg-black text-white text-[9px] font-bold px-2 py-1 uppercase tracking-widest">
        {id}
      </div>
    </div>
    <div className="space-y-2">
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{date}</div>
      <h4 className="text-lg font-black text-slate-900 leading-tight group-hover:text-orange-600 transition-colors uppercase tracking-tighter">{title}</h4>
      <div className="text-[10px] font-black uppercase tracking-widest border-b border-black inline-block pb-0.5">Read Audit Briefing</div>
    </div>
  </Link>
);

const Home: React.FC = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-48 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 border-l-4 border-black bg-slate-50 text-[10px] font-black uppercase tracking-[0.3em] text-slate-600 mb-10">
              Institutional Forensic Audit
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-slate-900 leading-[0.9] tracking-tighter mb-10">
              Evidence-Based <br/>
              <span className="text-slate-300">Institutional</span> <br/>
              Accountability.
            </h1>
            <p className="text-2xl text-slate-500 font-medium mb-12 max-w-2xl leading-relaxed">
              An independent research organization conducting forensic audits of corporations, AI systems, and regulatory bodies when institutional failure threatens public trust.
            </p>
            
            <div className="p-8 bg-slate-900 text-white rounded-none mb-16 border-l-[12px] border-slate-600">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4 text-slate-400">Legal Positioning</div>
              <p className="text-sm font-medium leading-relaxed opacity-90 italic">
                System Audit is registered in the United States to leverage First Amendment protections, providing a unique "Safe Harbor" for global reporting on matters of international consequence.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-6">
              <Link to="/report" className="px-10 py-5 bg-black text-white font-bold uppercase tracking-widest hover:bg-slate-800 transition-all text-center text-sm">
                Initiate Secure Disclosure
              </Link>
              <Link to="/commons" className="px-10 py-5 bg-white border border-slate-200 text-slate-900 font-bold uppercase tracking-widest hover:bg-slate-50 transition-all text-center text-sm">
                Enter The Commons
              </Link>
            </div>
          </div>
        </div>
        
        {/* Background Graphic */}
        <div className="absolute top-0 right-0 w-full h-full pointer-events-none opacity-[0.07] overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070" 
              alt="Professional architecture" 
              className="object-cover w-full h-full grayscale" 
            />
        </div>
      </section>

      {/* Primary Inquiries (New Section) */}
      <section className="py-32 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-16">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-orange-500 mb-4">Latest Briefings</div>
              <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Primary Forensic Inquiries</h2>
            </div>
            <Link to="/findings" className="text-xs font-bold uppercase tracking-widest border-b-2 border-black pb-1 hover:text-slate-500 hover:border-slate-500 transition-all">View All Results</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <ReportCard 
              id="RPT-01-2024"
              date="MAR 2024"
              title="The Geneva Drift: Algorithmic Policy Contagion"
              img="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2070"
            />
            <ReportCard 
              id="RPT-02-2024"
              date="JAN 2024"
              title="Pharma-Integrity Audit: Hidden Weighting in Trials"
              img="https://images.unsplash.com/photo-1576086213369-97a306dca365?q=80&w=2040"
            />
            <ReportCard 
              id="RPT-03-2023"
              date="DEC 2023"
              title="Agency Capture within Domestic Health Insurers"
              img="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=2070"
            />
          </div>
        </div>
      </section>

      {/* 4 Boxes Section */}
      <section className="py-32 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <InfoBox 
              icon="fa-shield-halved"
              title="Independent & Conflict-Free"
              desc="Our funding is strictly autonomous, precluding any conflicts of interest with insurers or vendors."
            />
            <InfoBox 
              icon="fa-scale-balanced"
              title="Forensic Standards"
              desc="All evidence handling follows strict chain-of-custody practices for potential legal proceedings."
            />
            <InfoBox 
              icon="fa-lock"
              title="Secure by Design"
              desc="Military-grade encryption (AES-256-GCM) protects all files and professional identities."
            />
            <InfoBox 
              icon="fa-microchip"
              title="Human + AI Oversight"
              desc="AI assists in pattern detection, but human analysts with domain expertise make all final judgments."
            />
          </div>
        </div>
      </section>

      {/* Global Corridor Quote */}
      <section className="py-40 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
            <i className="fas fa-quote-left text-slate-100 text-8xl absolute left-10 -mt-10 pointer-events-none"></i>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tight italic mb-12 max-w-4xl mx-auto">
                "The health of our global democracy depends upon the unwavering alignment between institutional mandates and the public interest; when bureaucratic drift occurs, forensic transparency becomes the essential tool for restoration."
            </h2>
            <Link to="/about" className="text-xs font-bold uppercase tracking-[0.4em] border-b-2 border-black pb-2 hover:text-slate-500 hover:border-slate-500 transition-all">
                The Global Mandate
            </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
